import PageTitle from '@/components/Admin/PageTitle';
import Input from '@/components/Admin/UI/Input';
import InputNumber from '@/components/Admin/UI/InputNumber';
import InputCurrency from '@/components/Admin/UI/InputCurrency';
import OptionBtn from '@/components/Admin/UI/OptionBtn';
import Select from '@/components/Admin/UI/Select';
import { colors } from '@/constants/colors';
import IconsLib from '@/utils/IconsLib';
import React, { useEffect, useState } from 'react';

import styled from 'styled-components';
import Btn from '../UI/Btn';
import { toast } from 'react-toastify';
import { SelectOption } from '@/types/shared';
import { createPayout } from '@/api/payout';
import { createVendoPayoutApi } from '@/api/vendo';
import { useUIStore } from '@/store/useUIStore';
import { bankData } from '@/utils/bankData';

interface Props {
   data: unknown;
}

// const bankData = [
//    { _id: 'bpi', name: 'BPI', srcIcon: '/bank/bpi.webp' },
//    { _id: 'bdo', name: 'BDO', srcIcon: '/bank/bdo.webp' },
//    { _id: 'metrobank', name: 'Metrobank', srcIcon: '/bank/metrobank.webp' },
//    { _id: 'unionbank', name: 'Unionbank', srcIcon: '/bank/unionbank.webp' },
//    {
//       _id: 'securitybank',
//       name: 'Security Bank',
//       srcIcon: '/bank/securitybank.webp',
//    },
//    { _id: 'rcbc', name: 'RCBC', srcIcon: '/bank/rcbc.webp' },
//    { _id: 'chinabank', name: 'China Bank', srcIcon: '/bank/chinabank.webp' },
//    { _id: 'dbp', name: 'DBP', srcIcon: '/bank/dbp.webp' },
//    { _id: 'pnb', name: 'PNB', srcIcon: '/bank/pnb.webp' },
//    {
//       _id: 'landbank',
//       name: 'Landbank',
//       srcIcon: '/bank/landbank.webp',
//    },
// ];

const eWalletData = [
   { _id: 'gcash', name: 'GCash', srcIcon: '/gcashpay.webp' },
];

export interface PayoutValues {
   accountName: string;
   accountNumber: string;
   bank: string;
   amount: string;
}

interface PayoutError {
   status: number;
   message: string;
}

const PayoutForm = ({ data }: Props) => {
   const { handleClose, handleRefresh } = useUIStore();
   const [isLoading, setIsLoading] = useState(false);
   const [activeTab, setActiveTab] = useState<'bank' | 'e-wallet'>('bank');
   const [values, setValues] = useState<PayoutValues>({
      accountName: '',
      bank: '',
      accountNumber: '',
      amount: '',
   });

   const { accountName, bank, accountNumber, amount } = values;

   const [errors, setErrors] = useState({
      accountName: false,
      bank: false,
      accountNumber: false,
      amount: false,
   });

   const handleTabSelect = (tab: 'bank' | 'e-wallet') => {
      setActiveTab(tab);
   };

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setValues((prev) => ({
         ...prev,
         [name]: value,
      }));

      setErrors((prev) => ({ ...prev, [name]: false }));
   };

   const validateForm = () => {
      const newErrors = {
         accountName: !accountName.trim(),
         bank: !bank.trim(),
         accountNumber: !accountNumber,
         amount: !amount,
      };
      setErrors(newErrors);
      return !Object.values(newErrors).some((error) => error);
   };

   const handleSubmit = async () => {
      if (isLoading) {
         return;
      }

      if (!validateForm()) {
         toast.error('Please fill in all fields');
         return;
      }

      try {
         setIsLoading(true);
         // Caller passes data={ source: 'vendo' } to route the payout to
         // the vendo Payout collection (source='vendo') instead of the
         // voucher-store one. Default behavior unchanged so every other
         // caller (voucher-store CardSales) still works.
         const source = (data as { source?: 'vendo' | 'voucher-store' } | null)
            ?.source ?? 'voucher-store';
         const response =
            source === 'vendo'
               ? await createVendoPayoutApi({
                    amount: Number(values.amount),
                    bank: values.bank,
                    accountNumber: values.accountNumber,
                    accountName: values.accountName,
                 })
               : await createPayout(values);

         if (response.status === 201) {
            toast.success(response.data.msg, {
               position: 'top-right',
               autoClose: 2500,
               hideProgressBar: false,
               closeOnClick: true,
               pauseOnHover: true,
               draggable: true,
            });

            setValues({
               accountName: '',
               bank: '',
               accountNumber: '',
               amount: '',
            });

            handleRefresh();
            handleClose();
         }
      } catch (err: unknown) {
         const error = err as PayoutError;
         toast.error(error.message || 'Failed to process payout request', {
            position: 'top-right',
            autoClose: 2500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
         });
      } finally {
         setIsLoading(false);
      }
   };

   const handleSelect = (item: SelectOption) => {
      setValues({ ...values, bank: item._id });
      setErrors((prev) => ({ ...prev, bank: false }));
   };

   useEffect(() => {
      // console.log(data);
      setValues({
         ...values,
         bank: bankData[0]._id,
      });
   }, []);
   // console.log(errors);
   console.log(`values`, values);
   return (
      <Wrapper>
         <Tab>
            <TabItem
               $active={activeTab === 'bank'}
               onClick={() => handleTabSelect('bank')}
            >
               <OptionBtn active={activeTab === 'bank'} />
               <TabItemContent>
                  <TabLabel $active={activeTab === 'bank'}>
                     Send to bank account
                  </TabLabel>
                  <IconsLib
                     icon="bank-line"
                     color={
                        activeTab === 'bank' ? colors.PRIMARY : colors.GRAY1
                     }
                     size="20px"
                  />
               </TabItemContent>
            </TabItem>
            <TabItem
               $active={activeTab === 'e-wallet'}
               onClick={() => handleTabSelect('e-wallet')}
            >
               <OptionBtn active={activeTab === 'e-wallet'} />
               <TabItemContent>
                  <TabLabel $active={activeTab === 'e-wallet'}>
                     Send to e-wallet
                  </TabLabel>
                  <IconsLib
                     icon="bank-card-line"
                     color={
                        activeTab === 'e-wallet' ? colors.PRIMARY : colors.GRAY1
                     }
                     size="20px"
                  />
               </TabItemContent>
            </TabItem>
         </Tab>

         <Form>
            <FormGroup>
               <Select
                  // key={activeTab}
                  title={activeTab === 'bank' ? 'Select bank' : 'E-wallet'}
                  data={activeTab === 'bank' ? bankData : eWalletData}
                  onChange={(item) => handleSelect(item)}
                  // onChange={(item) => setValues({ ...values, bank: item._id }) }
                  srcIconDefault="bank-line"
                  error={errors.bank}
               />
            </FormGroup>

            <FormGroup>
               {/* <FormLabel>
                  {activeTab === 'bank'
                     ? 'Fullname in the bank account'
                     : 'Recipient name'}
               </FormLabel> */}
               <Input
                  label={
                     activeTab === 'bank' ? 'Account Name' : 'Recipient name'
                  }
                  name="accountName"
                  value={accountName}
                  placeholder="e.g. Juan Dela Cruz"
                  onChange={handleChange}
                  error={errors.accountName}
               />
            </FormGroup>
            <FormGroup>
               <FormLabel>
                  {activeTab === 'bank'
                     ? 'Account Number'
                     : 'Mobile number linked to E-wallet'}
               </FormLabel>
               <InputNumber
                  name="accountNumber"
                  value={String(accountNumber)}
                  placeholder={
                     activeTab === 'bank'
                        ? '0000 0000 0000 0000'
                        : '09123456789'
                  }
                  onChange={handleChange}
                  error={errors.accountName}
               />
            </FormGroup>
            <FormGroup>
               <InputCurrency
                  label="Payout Amount"
                  name="amount"
                  value={String(amount)}
                  placeholder="Enter amount"
                  onChange={handleChange}
                  error={errors.amount}
               />
            </FormGroup>
         </Form>

         <BtnWrapper>
            <div onClick={handleClose}>
               <Btn
                  title="Cancel"
                  type="outline"
                  textcolor={colors.PRIMARY}
                  bgcolor={colors.PRIMARY}
                  size="small"
               />
            </div>
            <div onClick={handleSubmit}>
               <Btn
                  title={isLoading ? 'Please wait...' : 'Request'}
                  type={isLoading ? 'disabled' : 'normal'}
                  bgcolor={colors.PRIMARY}
                  textcolor="white"
                  size="small"
                  hasIcon={isLoading}
                  icon="loader"
                  iconPosition="right"
               />
            </div>
         </BtnWrapper>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   min-width: 350px;
   padding-top: 1rem;
`;

// Tab
// TabItem
// TabLabel

const Tab = styled.div`
   display: flex;
   gap: 10px;
`;
const TabItem = styled.div<{ $active: boolean }>`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px 1rem;
   border-radius: 4px;
   background-color: ${({ $active }) =>
      $active ? colors.BLUE_SUPER_LIGHT : colors.GRAY14};
   border: 1px solid ${({ $active }) => ($active ? colors.BLUE_LIGHT : 'none')};
   cursor: pointer;
`;

const TabLabel = styled.p<{ $active: boolean }>`
   font-size: 14px;
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY1)};
`;

const TabItemContent = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
`;

const FormGroup = styled.div`
   margin-top: 1rem;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;
const Form = styled.div`
   margin-top: 1rem;
`;
const FormLabel = styled.p`
   font-size: 14px;
   color: ${colors.GRAY1};
`;

const BtnWrapper = styled.div`
   margin-top: 1.5rem;
   display: flex;
   justify-content: flex-end;
   gap: 10px;
`;
export default PayoutForm;
